import { Suspense, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

import { STATION_TYPE } from '~common';
import { UnpinningNoteField } from '~components/atoms';
import { PaddingBox } from '~components/atoms/custom';
import { useQueryGetReason } from '~hooks/reason/useQueryGetReason';
import { UnpinningReasonKey } from '~types';

type ConfirmSwapStationProps = {
  currentStationImei: string | undefined;
  currentStationType: STATION_TYPE | undefined;
  newStationImei: string;
  newStationType: STATION_TYPE;
  swapReasonId: string | undefined;
  onChangeSwapReasonId: (value: string) => void;
  swapNote: string | undefined;
  onChangeSwapNote: (value: string) => void;
};

export const ConfirmSwapStation: React.FC<ConfirmSwapStationProps> = ({
  currentStationImei,
  currentStationType,
  newStationImei,
  newStationType,
  swapReasonId,
  onChangeSwapReasonId,
  swapNote,
  onChangeSwapNote,
}) => (
  <Suspense
    fallback={
      <Box
        display="flex"
        width="100%"
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    }
  >
    <SwapStationStepView
      currentStationImei={currentStationImei}
      currentStationType={currentStationType}
      newStationImei={newStationImei}
      newStationType={newStationType}
      swapReasonId={swapReasonId}
      onChangeSwapReasonId={onChangeSwapReasonId}
      swapNote={swapNote}
      onChangeSwapNote={onChangeSwapNote}
    />
  </Suspense>
);

type SwapStationStepViewProps = {
  currentStationImei: string | undefined;
  currentStationType: STATION_TYPE | undefined;
  newStationImei: string;
  newStationType: STATION_TYPE;
  swapReasonId: string | undefined;
  onChangeSwapReasonId: (value: string) => void;
  swapNote: string | undefined;
  onChangeSwapNote: (value: string) => void;
};

const SwapStationStepView: React.FC<SwapStationStepViewProps> = ({
  currentStationImei,
  currentStationType,
  newStationImei,
  newStationType,
  swapReasonId,
  onChangeSwapReasonId,
  swapNote,
  onChangeSwapNote,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeSwapReasonId(event.target.value);
  };

  const swapStationReason = useQueryGetReason({
    page: 0,
    size: 20,
    filter: {
      filterByKeys: [UnpinningReasonKey.UNPINNING_REASONS_FOR_STATION_SWAPS],
    },
  });

  // Preselect the first option, as the relocation step does. Every option is a
  // radio already on screen, so this saves a tap without hiding the rest.
  useEffect(() => {
    const firstReason = swapStationReason.data?.data?.[0];
    if (firstReason && !swapReasonId) {
      onChangeSwapReasonId(firstReason.id);
    }
  }, [swapStationReason.data?.data, swapReasonId, onChangeSwapReasonId]);

  return (
    <PaddingBox
      flex={1}
      sx={{
        bgcolor: 'background.paper',
        width: '100%',
        pt: 0,
        px: 0,
      }}
    >
      <Stack gap={3} pb={4} width="100%">
        {/* Title */}
        <Typography variant="h6" fontWeight={600}>
          You’re swapping
        </Typography>

        {/* Swapping Info */}
        <Stack gap={1.5} width="100%">
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              From
            </Typography>
            <Box textAlign="right">
              <Typography variant="body2" fontWeight={500}>
                IMEI: {currentStationImei || '---'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentStationType || '---'}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              To
            </Typography>
            <Box textAlign="right">
              <Typography variant="body2" fontWeight={500}>
                IMEI: {newStationImei || '---'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {newStationType || '---'}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Reason for Swap */}
        <Stack width="100%">
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Reason for Swap
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              // `?? ''` keeps the group controlled: with `undefined` MUI treats
              // it as uncontrolled and stops reflecting later prop changes, so
              // the preselected reason would never appear checked.
              value={swapReasonId ?? ''}
              onChange={handleChange}
              name="swap-reason"
              sx={{ gap: 1.5 }}
            >
              {(swapStationReason.data?.data ?? []).map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={
                    <Radio
                      disableRipple
                      sx={{
                        padding: 0,
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {option.reason}
                    </Typography>
                  }
                  sx={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    mx: 0,
                    my: 0.5,
                    '& .MuiFormControlLabel-label': {
                      flex: 1,
                      textAlign: 'left',
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Stack>

        <UnpinningNoteField
          label="Swap note"
          value={swapNote}
          onChange={onChangeSwapNote}
        />
      </Stack>
    </PaddingBox>
  );
};
